const AUTH_STORAGE_KEY = 'mindvrik_auth_user';
const AUTH_TOKEN_KEY = 'mindvrik_auth_token';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api';

export const ASSESSMENT_STATUSES = {
  notStarted: 'not_started',
  sessionCreated: 'session_created',
  redirectedToPartner: 'redirected_to_partner',
  inProgress: 'in_progress',
  submitted: 'submitted',
  waitingForReport: 'waiting_for_report',
  reportReady: 'report_ready',
};

export const assessmentStatusLabels = {
  [ASSESSMENT_STATUSES.notStarted]: 'Not Started',
  [ASSESSMENT_STATUSES.sessionCreated]: 'Session Created',
  [ASSESSMENT_STATUSES.redirectedToPartner]: 'Redirected to Partner',
  [ASSESSMENT_STATUSES.inProgress]: 'In Progress',
  [ASSESSMENT_STATUSES.submitted]: 'Submitted',
  [ASSESSMENT_STATUSES.waitingForReport]: 'Waiting for Report',
  [ASSESSMENT_STATUSES.reportReady]: 'Report Ready',
  unlocked: 'Not Started',
};

const readJson = (key, fallback) => {
  try {
    const stored = window.localStorage.getItem(key);
    return stored ? JSON.parse(stored) : fallback;
  } catch (error) {
    return fallback;
  }
};

const writeJson = (key, value) => {
  window.localStorage.setItem(key, JSON.stringify(value));
};

const request = async (path, options = {}) => {
  const token = window.localStorage.getItem(AUTH_TOKEN_KEY);
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch (error) {
    payload = null;
  }

  if (!response.ok) {
    throw new Error(payload?.detail || payload?.message || 'Request failed. Please try again.');
  }
  return payload;
};

const saveUser = (user) => {
  if (user) writeJson(AUTH_STORAGE_KEY, user);
  return user;
};

export const assessmentService = {
  async getEmailVerificationStatus() {
    return request('/email-verification/assessment/status');
  },

  async sendEmailVerificationCode() {
    return request('/email-verification/assessment/send', { method: 'POST' });
  },

  async verifyEmailCode(code) {
    return request('/email-verification/assessment/verify', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },

  async createAssessmentSession(user) {
    if (!user) throw new Error('Login is required to create an assessment session.');
    const session = await request('/assessments/sessions', { method: 'POST' });
    return session;
  },

  generateAssessmentRedirectUrl(session) {
    if (session?.missingConfig || !session?.redirectUrl) {
      return {
        url: '',
        missingConfig: true,
        message: session?.message || 'Assessment partner URL is not configured yet. Add ASSESSMENT_PARTNER_URL in backend .env when finalized.',
      };
    }

    return { url: session.redirectUrl, missingConfig: false };
  },

  storeAssessmentSession(user, session) {
    if (!user || !session) throw new Error('User and session are required.');
    return saveUser({
      ...user,
      assessmentSessionId: session.sessionId,
      assessmentStatus: session.status,
    });
  },

  getAssessmentStatus(user) {
    if (!user) return ASSESSMENT_STATUSES.notStarted;
    return user.assessmentStatus || ASSESSMENT_STATUSES.notStarted;
  },

  async updateAssessmentStatus(user, status) {
    if (!user) throw new Error('User is required.');
    if (!user.assessmentSessionId) {
      return saveUser({ ...user, assessmentStatus: status });
    }
    const updatedUser = await request(`/assessments/sessions/${user.assessmentSessionId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
    return saveUser(updatedUser);
  },

  getCurrentUser() {
    return readJson(AUTH_STORAGE_KEY, null);
  },
};
